import React, { useEffect, useState } from 'react';
import { getLeads, updateLead } from '../api/client';
import { C, Spinner, Toast, initials, useNotify } from '../utils/styles';

const STAGES = ['New','Contacted','Qualified','Proposal','Won','Lost'];
const STAGE_COLORS = { New:C.primary, Contacted:C.purple, Qualified:C.warning, Proposal:C.green, Won:C.success, Lost:C.danger };
const scoreColor = s => s>=80 ? C.success : s>=60 ? C.warning : C.danger;

export default function Pipeline() {
  const [leads,   setLeads]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState('All');
  const [dragId,  setDragId]  = useState(null);
  const [notification, notify] = useNotify();

  useEffect(() => {
    getLeads({ limit:200 })
      .then(({ data }) => setLeads(data.leads||[]))
      .catch(() => notify('Failed to load pipeline','error'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const onDragStart = (e, id) => { setDragId(id); e.dataTransfer.effectAllowed='move'; };
  const onDragOver  = (e) => { e.preventDefault(); e.dataTransfer.dropEffect='move'; };

  const onDrop = async (e, stage) => {
    e.preventDefault();
    if (!dragId) return;
    const lead = leads.find(l => l.id===dragId);
    if (!lead || lead.stage===stage) { setDragId(null); return; }
    try {
      await updateLead(dragId, { stage });
      setLeads(leads.map(l => l.id===dragId ? {...l, stage} : l));
      notify(`${lead.name} moved to ${stage}`);
    } catch { notify('Failed to move lead','error'); }
    setDragId(null);
  };

  const visibleStages  = filter==='All' ? STAGES : [filter];
  const stageLeads     = (s) => leads.filter(l => l.stage===s);
  const stageValue     = (s) => stageLeads(s).reduce((a,b) => a+(b.value||0), 0);

  if (loading) return <Spinner />;

  return (
    <div>
      <Toast {...notification} />

      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontSize:20, fontWeight:700, color:C.dark, marginBottom:2 }}>Pipeline</h1>
        <p style={{ fontSize:13, color:C.muted }}>Drag and drop leads between stages</p>
      </div>

      {/* Stage filter */}
      <div style={{ display:'flex', gap:8, marginBottom:20, flexWrap:'wrap' }}>
        {['All',...STAGES].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            style={{ padding:'6px 14px', borderRadius:20, border:`1px solid ${filter===s ? C.primary : C.border}`, background:filter===s ? C.primary : '#fff', color:filter===s ? '#fff' : C.text, fontSize:12, fontWeight:filter===s ? 600 : 400, cursor:'pointer' }}>
            {s}
            {s!=='All' && (
              <span style={{ marginLeft:6, background:filter===s?'rgba(255,255,255,0.25)':'var(--color-primary-light)', color:filter===s?'#fff':C.primary, borderRadius:20, padding:'1px 7px', fontSize:11 }}>
                {stageLeads(s).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Kanban */}
      <div style={{ display:'grid', gridTemplateColumns:`repeat(${visibleStages.length},minmax(200px,1fr))`, gap:12, overflowX:'auto', paddingBottom:8 }}>
        {visibleStages.map(stage => (
          <div key={stage} onDragOver={onDragOver} onDrop={e=>onDrop(e,stage)}
            style={{ background:'#f9fafb', borderRadius:12, border:`1px solid ${C.border}`, padding:12, minHeight:400 }}>

            <div style={{ marginBottom:12 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                <span style={{ fontSize:13, fontWeight:700, color:STAGE_COLORS[stage] }}>{stage}</span>
                <span style={{ background:`${STAGE_COLORS[stage]}22`, color:STAGE_COLORS[stage], borderRadius:20, padding:'2px 10px', fontSize:12, fontWeight:600 }}>
                  {stageLeads(stage).length}
                </span>
              </div>
              <div style={{ fontSize:11, color:C.faint }}>₹{(stageValue(stage)/1000).toFixed(0)}K pipeline</div>
              <div style={{ height:3, borderRadius:2, background:STAGE_COLORS[stage], marginTop:8, opacity:0.4 }} />
            </div>

            {stageLeads(stage).map(l => (
              <div key={l.id} draggable onDragStart={e=>onDragStart(e,l.id)}
                style={{ background:'#fff', borderRadius:10, border:`1px solid ${dragId===l.id?C.primary:C.border}`, padding:12, marginBottom:8, cursor:'grab', opacity:dragId===l.id?0.5:1, transition:'all 0.15s', boxShadow:dragId===l.id?`0 4px 12px var(--color-primary-light)`:'none' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                  <div style={{ width:28, height:28, borderRadius:'50%', background:C.primary, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:10, fontWeight:700, flexShrink:0 }}>
                    {initials(l.name)}
                  </div>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:12, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{l.name}</div>
                    <div style={{ fontSize:11, color:C.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{l.company}</div>
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                  <span style={{ fontSize:12, fontWeight:700, color:C.primary }}>₹{(l.value/1000).toFixed(1)}K</span>
                  {l.ai_score && <span style={{ fontSize:11, fontWeight:700, color:scoreColor(l.ai_score) }}>{l.ai_score}</span>}
                </div>
                {l.ai_score && (
                  <div style={{ background:'#f3f4f6', borderRadius:4, height:3 }}>
                    <div style={{ width:`${l.ai_score}%`, height:3, borderRadius:4, background:scoreColor(l.ai_score) }} />
                  </div>
                )}
                {l.tags?.length>0 && (
                  <div style={{ display:'flex', gap:4, flexWrap:'wrap', marginTop:8 }}>
                    {l.tags.slice(0,2).map(t => (
                      <span key={t} style={{ background:'var(--color-primary-light)', color:C.primary, borderRadius:20, padding:'1px 8px', fontSize:10 }}>{t}</span>
                    ))}
                    {l.tags.length>2 && <span style={{ background:'#f3f4f6', color:C.faint, borderRadius:20, padding:'1px 8px', fontSize:10 }}>+{l.tags.length-2}</span>}
                  </div>
                )}
                <div style={{ fontSize:10, color:C.faint, marginTop:6, display:'flex', alignItems:'center', gap:4 }}>
                  <i className="ti ti-antenna" style={{ fontSize:11 }} />{l.source}
                </div>
              </div>
            ))}

            {stageLeads(stage).length===0 && (
              <div style={{ textAlign:'center', padding:'30px 10px', color:'#d1d5db', fontSize:12 }}>
                <i className="ti ti-drag-drop" style={{ fontSize:24, display:'block', marginBottom:6 }} />Drop leads here
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
